import { ReactElement, useState } from 'react'
import { Stack, TextField } from '@mui/material'
import { TagsField } from '@ui2/forms'
import useUpdatePlanMetadata from '../../hooks/useUpdatePlanMetadata'

interface Props {
  plan: Plan
}

/**
 * Kommentar and Tags of a saved plan, edited in place below its row in the "Pläne" tab and saved
 * automatically: tags when added or removed, the comment on blur or Enter (Shift+Enter adds a new
 * line). Each save sends only the changed field and is applied to the cached plan optimistically
 * (so the fields render from the cache and e.g. the PDF save already sends the new values); a
 * failed request refetches the plans, which reverts the fields to what the server has.
 */
const PlanInformationForm = ({ plan }: Props): ReactElement => {
  const { updatePlanMetadata } = useUpdatePlanMetadata()
  const tags = plan.tags ?? []
  const comment = plan.comment ?? ''
  // The comment while it is being typed; null once committed (then the cached value is shown).
  const [commentDraft, setCommentDraft] = useState<string | null>(null)

  const commitComment = () => {
    if (commentDraft !== null && commentDraft !== comment) {
      updatePlanMetadata(plan, { comment: commentDraft })
    }
    setCommentDraft(null)
  }

  return (
    <Stack spacing={1.5} data-cy="plan-view-plan-information-form">
      <TagsField value={tags} onChange={nextTags => updatePlanMetadata(plan, { tags: nextTags })} />
      <TextField
        label="Kommentar"
        value={commentDraft ?? comment}
        onChange={event => setCommentDraft(event.target.value)}
        onBlur={commitComment}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            // Blurring commits (see onBlur).
            ;(event.target as HTMLElement).blur()
          }
        }}
        multiline
        rows={3}
        fullWidth
        size="small"
        placeholder="Kommentar"
      />
    </Stack>
  )
}

export default PlanInformationForm
